import { types } from 'mobx-state-tree';
import moment from 'moment';
import { mg } from '../../mg/mg';
import { Momentino } from '../../services/Momentino';

export const OrderOptionsStore = types
	.model('OrderOptionsStore', {
		templateId: types.identifier,
		_supplier: types.frozen(),
		isPickup: false,
		pickupLocation: '',
		paymentMethod: '',
		date: '',
		time: '',
		message: '',
		orderReference: '',
		hasCoupon: false,
		coupon: '',
		tooLate: false,
	})
	.views(self => ({
		get datetimePickerType() : 'NOTHING' | 'DATE' | 'DATETIME' {
			return self.isPickup ? self._supplier.pickupTiming : self._supplier.deliveryTiming;
		},
		get toPrototype() {
			return {
				templateId: self.templateId,
				pickupLocation: self.isPickup ? self.pickupLocation : undefined,
				paymentMethod: self.paymentMethod || undefined,
				date: self.date || undefined,
				time: self.time || undefined,
				message: self.message,
				orderReference: self.orderReference,
			};
		},
	}))
	.views(self => ({
		get datetimePickerTypeForHtml() {
			switch (self.datetimePickerType) {
			case 'DATETIME': return 'datetime-local';
			case 'DATE': return 'date';
			default: return 'text';
			}
		},
	}))
	.views(self => ({
		get canPickup() {
			return !!self._supplier.pickupLocations;
		},
		get pickupLocationsList() {
			const s = self._supplier.pickupLocations;
			return !self.isPickup ? [''] : s.trim().split(/\s*,\s*/);
		},
		get datetime() {
			const {date, time} = self;
			switch (self.datetimePickerType) {
			case 'DATETIME': return date && time ? date + 'T' + time : '';
			case 'DATE': return date;
			default: return '';
			}
		},
		get paymentMethods() {
			const o = self._supplier.paymentOptions;
			switch (o) {
			case 'ON_ACCOUNT': return ['ON_ACCOUNT'];
			case 'CASH': return ['CASH'];
			case 'ON_ACCOUNT_OR_CASH': return ['ON_ACCOUNT', 'CASH'];
			case 'NOTHING': return ['UNSPECIFIED'];
			}
			console.error('Unknown paymentOptions', o);
			return ['UNSPECIFIED'];
		},
		get canOrderMessage() {
			return !!self._supplier.allowsOrderMessage;
		},
		get orderReferenceLength() {
			return self._supplier.orderReferenceLength || 0;
		},
	}))
	.actions(self => ({
		_fixDate() {
			if (self.date.match(/^\d\d\d\d-\d\d-\d\d$/)) return;
			self.date = moment().add(1, 'd').format('YYYY-MM-DD');
		},
		_fixTime() {
			if (self.time.length === 5) self.time += ':00';
			if (self.time.match(/^\d\d:\d\d:\d\d$/)) return;
			self.time = '10:00:00';
		},
	}))
	.actions(self => ({
		_applySupplierInternal() {
			const s = self._supplier;
			if (!s) throw new Error('Unset supplier in: ' + 'OrderOptionsStore');
			if (!self.canPickup) self.isPickup = false;
			if (!s.pickupLocations || s.pickupLocations.indexOf(self.pickupLocation) < 0) self.pickupLocation = '';
			if (!self.canOrderMessage) self.message = '';
			self.orderReference = self.orderReference.substring(0, self.orderReferenceLength);
			switch (self.datetimePickerType) {
			case 'DATETIME':
				self._fixDate();
				self._fixTime();
				self.tooLate = moment().format('YYYY-MM-DD hh:mm:ss') > self.date + ' ' + self.time;
				break;
			case 'DATE':
				self._fixDate();
				self.time = '';
				self.tooLate = moment().format('YYYY-MM-DD') > self.date;
				break;
			default:
				self.date = '';
				self.time = '';
				self.tooLate = false;
			}
			self.hasCoupon = s.hasCoupon;
		}
	}))
	.actions(self => ({
		setIsPickup(isPickup) {
			self.isPickup = isPickup;
			self._applySupplierInternal();
		},
		setPickupLocation(pickupLocation) {
			self.pickupLocation = pickupLocation;
			self._applySupplierInternal();
		},
		setPaymentMethod(paymentMethod) {
			self.paymentMethod = paymentMethod;
			self._applySupplierInternal();
		},
		setMessage(message) {
			self.message = message;
			self._applySupplierInternal();
		},
		setOrderReference(orderReference) {
			self.orderReference = orderReference;
			self._applySupplierInternal();
		},
		setDatetime(datetime) {
			if (datetime._isAMomentObject) {
				switch (self.datetimePickerType) {
				case 'DATETIME':
					self.date = Momentino.momentTo.date(datetime);
					self.time = Momentino.momentTo.time(datetime);
					break;
				case 'DATE':
					self.date = Momentino.momentTo.date(datetime);
					self.time = '';
					break;
				default:
					self.date = '';
					self.time = '';
					break;
				}
			} else {
				switch (self.datetimePickerType) {
				case 'DATETIME':
					self.date = datetime.replace(/T.*/, '');
					self.time = datetime.replace(/.*T/, '');
					break;
				case 'DATE':
					self.date = datetime;
					self.time = '';
					break;
				default:
					self.date = '';
					self.time = '';
					break;
				}
			}
			self._applySupplierInternal();
		},
		setCoupon(coupon) {
			self.coupon = coupon;
			self._applySupplierInternal();
		},
	}))
	.actions(self => ({
		applySupplier(supplier) {
			self._supplier = mg.freezeIfDevelopment({...supplier});
			self._applySupplierInternal();
		},
	}));
